import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import { useRef, useState } from "react";
import { actSubjects } from "../../actprep-ui/constants/program";
import {
  capitalize,
  formatConstantOptions,
} from "../../actprep-ui/utils/formatting";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Switch,
} from "@mui/material";
import { testingVariants } from "../../actprep-ui/constants/testing";
import ModelAutoComplete from "../../actprep-ui/components/ModelAutoComplete";

const EXCLUDED_TEST_FIELDS = ["answers", "conversion_chart", "id", "lesson"];

const DEFAULT_FIELDS = {
  name: "",
  visible_name: "",
  test_location: "",
  variant: "",
  lesson_question_count: "",
  lesson_first_question: 1,
  lesson_time_allowed: "",
  lesson_id: null,
  is_interactive: false,
  show_location_link: false,
};

actSubjects.forEach((subject) => {
  DEFAULT_FIELDS[`${subject}_location`] = "";
  DEFAULT_FIELDS[`${subject}_answers`] = "";
  DEFAULT_FIELDS[`${subject}_order`] = undefined;
});

export default function TestInfoForm({
  test,
  isCreateMode,
  onEdit,
  onCreate,
  error,
  setError,
}) {
  const initialTest = useRef(test);
  const [isChanged, setIsChanged] = useState(false);
  const [loading, setIsLoading] = useState(false);
  const isEditMode = !!test;

  const getTestField = (test, field) => {
    if (test[field] == undefined || test[field] == null) {
      return "";
    }
    return test[field];
  };
  const getTestFormData = (test = {}) => {
    return {
      ...DEFAULT_FIELDS,
      ...Object.fromEntries(
        Object.keys(test)
          .filter((field) => !EXCLUDED_TEST_FIELDS.includes(field))
          .map((field) => [field, getTestField(test, field)])
      ),
      lesson_id: test.lesson
        ? {
            uuid: test.lesson.uuid,
            label: test.lesson.name,
          }
        : null,
      lesson_first_question: test.lesson_first_question || 1,
    };
  };

  const [formData, setFormData] = useState(getTestFormData(test));
  const handleFormChange = (field) => (event) => {
    if (!isChanged) {
      setIsChanged(true);
    }
    setFormData((formData) => ({ ...formData, [field]: event.target.value }));
  };
  const handleReset = () => {
    setFormData(getTestFormData(initialTest.current));
    setIsChanged(false);
    setError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);
    const finalFormData = {
      ...formData,
      lesson_id: formData.lesson_id?.uuid || null,
    };
    if (isCreateMode && onCreate) {
      await onCreate(finalFormData);
    } else if (onEdit) {
      await onEdit(test.uuid, finalFormData);
    }
    setIsChanged(false);
    setIsLoading(false);
  };
  return (
    <Box component={"form"} onSubmit={handleSubmit}>
      <Grid container spacing={3} mt={1} sx={{ width: "100%" }}>
        <Grid size={12}>
          <Typography variant="h6" fontWeight={"bold"}>
            Basic Info
          </Typography>
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            value={formData.name}
            onChange={handleFormChange("name")}
            label="Name"
            variant="standard"
            required
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            value={formData.visible_name}
            onChange={handleFormChange("visible_name")}
            label="Visible Name"
            helperText="The name students will be shown"
            variant="standard"
          />
        </Grid>
        <Grid size={6}>
          <TextField
            fullWidth
            value={formData.test_location}
            onChange={handleFormChange("test_location")}
            label="Location"
            variant="standard"
          />
        </Grid>
        <Grid size={6}>
          <FormControl
            sx={{
              height: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={formData.show_location_link ?? false}
                  onChange={(event, checked) => {
                    setIsChanged(true);
                    setFormData((formData) => ({
                      ...formData,
                      show_location_link: checked,
                    }));
                  }}
                  color="secondary"
                />
              }
              label="Show Location Link"
            />
            <FormHelperText>
              Check this to link the student directly to the test from their
              schedule. This should not be checked for official ACT tests.
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid size={6}>
          <FormControl fullWidth variant="standard">
            <InputLabel>Variant</InputLabel>
            <Select
              value={formData.variant}
              label="Challenge Option"
              required
              onChange={handleFormChange("variant")}
            >
              {formatConstantOptions(testingVariants).map(
                ({ label, value }, i) => (
                  <MenuItem key={i} value={value}>
                    {label}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={6}>
          <FormControl
            sx={{
              height: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_interactive ?? false}
                  onChange={(event, checked) => {
                    setIsChanged(true);
                    setFormData((formData) => ({
                      ...formData,
                      is_interactive: checked,
                    }));
                  }}
                  color="secondary"
                />
              }
              label="Interactive"
            />
            <FormHelperText>
              Interactive tests give students instant results when they answer a
              question.
            </FormHelperText>
          </FormControl>
        </Grid>
        {["classic", "2025"].includes(formData.variant) && (
          <>
            <Grid size={12}>
              <Typography variant="h6" fontWeight={"bold"}>
                Subject Test info
              </Typography>
            </Grid>
            {actSubjects.map((subject) => (
              <Grid container spacing={1} size={12} key={subject}>
                <Grid size={12}>
                  <Typography variant="overline" fontWeight={"bold"}>
                    {capitalize(subject)} Info
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <TextField
                    fullWidth
                    value={formData[`${subject}_location`]}
                    onChange={handleFormChange(`${subject}_location`)}
                    label="Location"
                    variant="standard"
                    size="small"
                  />
                </Grid>
                <Grid size={4}>
                  <TextField
                    fullWidth
                    value={formData[`${subject}_answers`]}
                    onChange={handleFormChange(`${subject}_answers`)}
                    label="Answers Location"
                    variant="standard"
                    size="small"
                  />
                </Grid>
                <Grid size={4}>
                  <TextField
                    fullWidth
                    value={formData[`${subject}_order`]}
                    onChange={handleFormChange(`${subject}_order`)}
                    label="Order"
                    variant="standard"
                    size="small"
                    type="number"
                  />
                </Grid>
              </Grid>
            ))}
          </>
        )}
        {formData.variant == "lesson" && (
          <>
            <Grid size={12}>
              <Typography variant="h6" fontWeight={"bold"}>
                Lesson Test info
              </Typography>
            </Grid>
            <Grid size={12}>
              <ModelAutoComplete
                modelName={"Lesson"}
                label={"Lesson"}
                value={formData.lesson_id}
                setValue={(newVal) => {
                  setIsChanged(true);
                  setFormData((currFormData) => ({
                    ...currFormData,
                    lesson_id: newVal,
                  }));
                }}
                required
              />
            </Grid>
            <Grid size={4}>
              <TextField
                fullWidth
                value={formData.lesson_first_question}
                onChange={handleFormChange("lesson_first_question")}
                label="First Question"
                helperText="Question number this section starts on"
                variant="standard"
                size="small"
                type="number"
                slotProps={{ htmlInput: { min: 1 } }}
                required
              />
            </Grid>
            <Grid size={4}>
              <TextField
                fullWidth
                value={formData.lesson_question_count}
                onChange={handleFormChange("lesson_question_count")}
                label="Question Count"
                helperText={(() => {
                  const first = parseInt(formData.lesson_first_question, 10);
                  const count = parseInt(formData.lesson_question_count, 10);
                  if (!(first > 0 && count > 0)) {
                    return "How many questions are in this section";
                  }
                  return `Covers questions ${first}–${first + count - 1} (${count} questions)`;
                })()}
                variant="standard"
                size="small"
                type="number"
                slotProps={{ htmlInput: { min: 1 } }}
                required
              />
            </Grid>
            <Grid size={4}>
              <TextField
                fullWidth
                value={formData.lesson_time_allowed}
                onChange={handleFormChange("lesson_time_allowed")}
                label="Time Allowed (minutes)"
                variant="standard"
                size="small"
                type="number"
              />
            </Grid>
          </>
        )}
        {error && (
          <Grid size={12}>
            <Alert sx={{ mt: 1 }} severity="error">
              {error}
            </Alert>
          </Grid>
        )}
        <Grid size={12}>
          <ButtonGroup fullWidth disabled={!isChanged}>
            {isEditMode && <Button onClick={handleReset}>Reset</Button>}
            <LoadingButton type="submit" loading={loading}>
              {isEditMode ? "Save" : "Create Test"}
            </LoadingButton>
          </ButtonGroup>
        </Grid>
      </Grid>
    </Box>
  );
}
